<template>
  <div class="card">
    <div class="card-body">
      <div class="soft-gradient p-3 rounded d-flex align-items-center justify-content-between mb-4">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-2 rounded-2 me-2">
            <vue-feather type="award" stroke="#652aa3" size="20"></vue-feather>
          </div>
          <h5 class="mb-0">Manager Review</h5>
        </div>
        <div v-if="review?.status">
          <span :class="getStatusBadgeClass(review.status)" class="p-2">
            {{ formatStatus(review.status) }}
          </span>
        </div>
      </div>

      <div v-if="review.summary || review.strengths || review.areas_for_improvement || review.development_plan || review.manager_comments || review.overall_score !== null || review.rating">
        <div class="mb-3 p-3 rounded border bg-light" v-if="review.summary">
          <h6 class="text-primary mb-1">Summary</h6>
          <p class="mb-0">{{ review.summary }}</p>
        </div>

        <div class="mb-3 p-3 rounded border bg-light" v-if="review.strengths">
          <h6 class="text-primary mb-1">Strengths</h6>
          <p class="mb-0">{{ review.strengths }}</p>
        </div>

        <div class="mb-3 p-3 rounded border bg-light" v-if="review.areas_for_improvement">
          <h6 class="text-primary mb-1">Areas for Improvement</h6>
          <p class="mb-0">{{ review.areas_for_improvement }}</p>
        </div>

        <div class="mb-3 p-3 rounded border bg-light" v-if="review.development_plan">
          <h6 class="text-primary mb-1">Development Plan</h6>
          <p class="mb-0">{{ review.development_plan }}</p>
        </div>

        <div class="mb-3 p-3 rounded border bg-light" v-if="review.manager_comments">
          <h6 class="text-primary mb-1">Manager Comments</h6>
          <p class="mb-0">{{ review.manager_comments }}</p>
        </div>

        <div class="row" v-if="review.overall_score !== null || review.rating">
          <div class="col-md-6" v-if="review.overall_score !== null">
            <div class="text-muted small mb-1">Overall Score</div>
            <div class="progress rounded-pill mb-2" style="height: 20px;">
              <div
                class="progress-bar"
                :class="getScoreBarClass(review.overall_score)"
                :style="`width: ${review.overall_score}%`"
              >
                {{ review.overall_score }}%
              </div>
            </div>
          </div>
          <div class="col-md-6" v-if="review.rating">
            <h6 class="text-primary">Rating</h6>
            <span :class="getRatingBadgeClass(review.rating)">
              {{ formatRating(review.rating) }}
            </span>
          </div>
        </div>
      </div>

      <div v-else class="alert alert-info">
        <i class="icon-feather-info me-2"></i>
        Manager review has not been submitted yet.
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ManagerReviewTab',
  props: {
    review: {
      type: Object,
      required: true
    }
  },
  setup() {
    const formatStatus = (status) => {
      const statusMap = {
        pending: 'Pending',
        self_review: 'Self Review',
        manager_review: 'Manager Review',
        calibration: 'Calibration',
        completed: 'Completed'
      };
      return statusMap[status] || status;
    };

    const formatRating = (rating) => {
      const ratingMap = {
        exceptional: 'Exceptional',
        exceeds: 'Exceeds Expectations',
        meets: 'Meets Expectations',
        needs_improvement: 'Needs Improvement',
        unsatisfactory: 'Unsatisfactory'
      };
      return ratingMap[rating] || rating;
    };

    const getStatusBadgeClass = (status) => {
      const classes = {
        pending: 'badge bg-secondary',
        self_review: 'badge bg-warning',
        manager_review: 'badge bg-info',
        calibration: 'badge bg-primary',
        completed: 'badge bg-success'
      };
      return classes[status] || 'badge bg-secondary';
    };

    const getRatingBadgeClass = (rating) => {
      const classes = {
        exceptional: 'badge bg-success',
        exceeds: 'badge bg-primary',
        meets: 'badge bg-info',
        needs_improvement: 'badge bg-warning',
        unsatisfactory: 'badge bg-danger'
      };
      return classes[rating] || 'badge bg-secondary';
    };

    const getScoreBarClass = (score) => {
      if (score >= 90) return 'bg-success';
      if (score >= 75) return 'bg-primary';
      if (score >= 60) return 'bg-info';
      if (score >= 40) return 'bg-warning';
      return 'bg-danger';
    };

    return {
      formatStatus,
      formatRating,
      getStatusBadgeClass,
      getRatingBadgeClass,
      getScoreBarClass
    };
  }
};
</script>

<style scoped>
/* Local copies of shared helpers used in header */
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.08);
}
.soft-gradient {
  background: linear-gradient(135deg, rgba(101,42,163,0.06) 0%, rgba(13,110,253,0.04) 100%);
  border-radius: 0.5rem;
}
</style>
